import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuth, primaryShell } from "@/hooks/use-auth";
import { LoadingState } from "@/components/ui/loading-state";
import { logger } from "@/lib/logger";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/")({
  component: Index,
});

function Index() {
  const { loading, user, roles, authError } = useAuth();
  const navigate = useNavigate();
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    logger.info("[index] mount", { loading, hasUser: !!user, roles: roles.length });
    const t = setTimeout(() => setSlow(true), 6000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (loading) return;
    logger.info("[index] resolve route", { hasUser: !!user, roles: roles.length });
    if (!user) {
      navigate({ to: "/login" });
      return;
    }
    const shell = primaryShell(roles);
    if (shell === "app") {
      // Route app-shell users through the Implementation Experience until
      // the tenant has selected a Blueprint. Once Blueprint is set, land
      // them on Scoreboard as before.
      (async () => {
        const { data } = await supabase
          .from("tenants")
          .select("blueprint")
          .limit(1)
          .maybeSingle();
        if (!data?.blueprint) navigate({ to: "/app/implementation" });
        else navigate({ to: "/scoreboard" });
      })();
    } else if (shell === "portal") navigate({ to: "/portal/workspace" });
    // New self-serve users have no tenant role yet — send them to create one
    // rather than the dead-end "no access" screen.
    else navigate({ to: "/setup" });
  }, [loading, user, roles, navigate]);

  if (authError && !loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold tracking-tight">We couldn't sign you in</h1>
          <p className="mt-2 text-sm text-muted-foreground">{authError}</p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <button
              onClick={() => location.reload()}
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Try again
            </button>
            <a
              href="/login"
              className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
            >
              Go to sign in
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4 text-center px-4">
        <LoadingState label="Loading…" />
        {slow && (
          <div className="max-w-sm text-xs text-muted-foreground">
            Still working… if this screen persists,{" "}
            <button
              className="underline underline-offset-2"
              onClick={() => location.reload()}
            >
              refresh the page
            </button>{" "}
            or{" "}
            <a href="/login" className="underline underline-offset-2">
              sign in again
            </a>
            .
          </div>
        )}
      </div>
    </div>
  );
}
